import { Modal } from '@mantine/core';
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import InputField from '../InputField/InputField';

interface TypeToConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    title: ReactNode;
    /**
     * Body above the confirmation field: what will happen, plus any extra fields. Mark the first
     * extra field `data-autofocus` (only while it is enabled) so it takes initial focus.
     */
    children?: ReactNode;
    /** Text the user must type; compared after trimming, case-sensitive. */
    expected: string;
    inputLabel: string;
    /** Instruction under the label, e.g. "Type <name> to confirm". */
    inputDescription: ReactNode;
    confirmLabel: string;
    confirmIcon?: ReactNode;
    confirmVariant?: 'primary' | 'danger';
    isSubmitting?: boolean;
    /** Extra gate checked after the typed text matches; return false (and flag your own field) to stop. */
    validate?: () => boolean;
    /** Server error from the last attempt; announced as an alert. */
    error?: ReactNode;
    onConfirm: () => void;
}

/**
 * Dialog for irreversible or high-impact actions: the user types `expected` to confirm. Initial
 * focus is on the first `data-autofocus` field in `children`, else the confirmation field; paste
 * is allowed, and Esc/outside click are ignored while submitting.
 */
export default function TypeToConfirmModal({
    opened,
    onClose,
    title,
    children,
    expected,
    inputLabel,
    inputDescription,
    confirmLabel,
    confirmIcon,
    confirmVariant = 'danger',
    isSubmitting = false,
    validate,
    error,
    onConfirm,
}: TypeToConfirmModalProps) {
    const { t } = useTranslation('common');
    const [value, setValue] = useState('');
    const [mismatch, setMismatch] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!opened) return;
        setValue('');
        setMismatch(false);
    }, [opened]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isSubmitting) return;
        if (value.trim() !== expected) {
            setMismatch(true);
            inputRef.current?.focus();
            return;
        }
        if (validate && !validate()) return;
        onConfirm();
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={isSubmitting ? () => {} : onClose}
            centered
            closeOnClickOutside={!isSubmitting}
            closeOnEscape={!isSubmitting}
            closeButtonProps={{ 'aria-label': t('actions.close'), disabled: isSubmitting }}
            title={<span className="text-base font-bold">{title}</span>}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
        >
            <form
                onSubmit={handleSubmit}
                noValidate
                className="flex flex-col justify-start items-stretch gap-4"
            >
                {children}

                <InputField
                    ref={inputRef}
                    data-autofocus
                    size="md"
                    width="100%"
                    label={inputLabel}
                    description={inputDescription}
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value);
                        if (mismatch) setMismatch(false);
                    }}
                    readOnly={isSubmitting}
                    autoComplete="off"
                    spellCheck={false}
                    error={mismatch ? t('typeToConfirm.mismatch', { expected }) : undefined}
                />

                {error && (
                    <div role="alert" className="text-sm text-fg">
                        {error}
                    </div>
                )}

                <div className="w-full flex flex-row flex-wrap justify-end items-center gap-2">
                    <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                        {t('actions.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant={confirmVariant}
                        leftIcon={confirmIcon}
                        isLoading={isSubmitting}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
